/**
 * UI Store factory.
 *
 * State for theme, modals, toasts, sidebar, drafts, filters, etc.
 * The DOM-specific bits (system theme detection, applying the theme to
 * `<html>`) are pushed behind a ThemeAdapter so the same store works on web
 * and React Native (where ThemeAdapter wraps NativeWind's colorScheme).
 *
 * IMPORTANT: Call this **exactly once per app process** — see
 * createAuthStore.ts for the rationale.
 */
import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import type { ExampleScenario } from '../fixtures/exampleScenarios';
import type { StorageAdapter } from '../platform/storage';
import type { ThemeAdapter } from '../platform/theme';

export type ExpenseFilterMode = 'all' | 'involved' | 'paidByMe';

export type ConfettiIntensity = 'subtle' | 'medium' | 'high';

/** How many recently-used currencies the picker keeps pinned. */
export const MAX_RECENT_CURRENCIES = 5;

/** After a scan that read items: the group's own split, or everyone picks. */
export type ScanSplitChoice = 'default' | 'everyonePicks';

export interface Toast {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  duration?: number;
}

interface UIState {
  theme: 'light' | 'dark' | 'system';
  effectiveTheme: 'light' | 'dark';
  sidebarOpen: boolean;
  modals: {
    createExpense: boolean;
    inviteMembers: boolean;
    settings: boolean;
    deleteConfirm: boolean;
  };
  toasts: Toast[];
  loading: {
    global: boolean;
    actions: Record<string, boolean>;
  };
  activeGroupId: string | null;
  activeTab: string;
  isChangingRoute: boolean;
  formDrafts: {
    expense?: Record<string, unknown>;
    group?: Record<string, unknown>;
    [key: string]: Record<string, unknown> | undefined;
  };
  activityFilter: ExpenseFilterMode;
  expensesFilter: ExpenseFilterMode;
  /**
   * Whether the canvas-confetti celebration fires on milestone
   * actions (expense create/update, settlement record, group
   * create, invite accept, etc.). Defaults to true. Honored by
   * both web's `celebrate()` (apps/web/src/utils/confetti.ts) and
   * mobile's `useConfetti()` (apps/mobile/src/components/ConfettiOverlay.tsx).
   *
   * Local-only preference — same pattern as `theme`. A user who
   * dismisses confetti on their phone keeps it dismissed only on
   * that device; no server round-trip.
   */
  celebrationsEnabled: boolean;
  /**
   * Which celebration intensity fires on milestone actions when
   * `celebrationsEnabled` is on. User-selectable in Fiesta Mode settings
   * (subtle / medium / high); defaults to `subtle`. Mobile's `celebrate()`
   * resolves to this when a call site passes no explicit intensity.
   * Local-only preference — same pattern as `theme` / `celebrationsEnabled`.
   */
  confettiIntensity: ConfettiIntensity;
  /**
   * Whether cloud (Anthropic) category suggestions are allowed. The
   * request carries only the typed title + locale — never the user's
   * identity — so this ships default-ON with disclosure in the privacy
   * policy (Apple 5.1.2(i)); the Settings toggle is how a user declines.
   * Off → prediction falls back to the on-device keyword path.
   * Local-only preference (per device) — same pattern as `theme`.
   */
  cloudPredictionEnabled: boolean;
  /**
   * The read-only example group ("Weekend in Lisbon") sits below the
   * user's own groups until the user hides it (swipe on mobile, × on
   * web); a small link restores it. It exists so a new user sees what a
   * populated group looks like before inviting anyone (tester feedback
   * 2026-09-11).
   *
   * Persisted here so the list answers instantly and offline, but the
   * server's user settings are the source of truth across devices:
   * `setExampleGroupHidden` also writes through a registered persister,
   * and the loaded profile's value is mirrored back in via
   * `applyServerExampleGroupHidden`.
   */
  exampleGroupHidden: boolean;
  /**
   * How many times the app has been launched (cold start / page load)
   * with the example group feature present. The example rotates through
   * its scenarios one per launch (`selectExampleScenario` in core maps a
   * count to a scenario); persisted so the rotation continues across
   * restarts. Recorded once per launch by `ExampleGroupSession`.
   */
  exampleGroupLaunches: number;
  /**
   * The scenario picked from the example's chips, over the launch
   * rotation. Never persisted: the next launch rotates as usual.
   */
  exampleScenarioOverride: ExampleScenario['key'] | null;
  /**
   * Most-recently chosen currency codes, newest first, capped at
   * {@link MAX_RECENT_CURRENCIES}. Feeds the pinned section at the top of
   * mobile's currency picker so the handful of currencies a traveller
   * actually uses stay one tap away instead of ~190 rows down.
   *
   * Local-only preference (per device), like `theme` — never synced.
   */
  recentCurrencies: string[];
  /**
   * Job id of a Splitwise import started on this device that has not yet
   * been seen through to a terminal status. Persisted so leaving the flow —
   * or restarting the app — mid-import offers the way back to the progress
   * screen instead of quietly inviting a second import of the same file.
   *
   * Local-only (per device), like `theme`: the job itself lives server-side.
   */
  activeImportJobId: string | null;
  /**
   * The post-scan choice last taken in each group, offered first the next
   * time. Per device and cleared on sign-out: it is the person's habit, and
   * the next person on this phone has their own.
   */
  scanSplitChoiceByGroup: Record<string, ScanSplitChoice>;
}

interface UIActions {
  setTheme: (theme: UIState['theme']) => void;
  toggleTheme: () => void;
  applyTheme: (theme: 'light' | 'dark') => void;

  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;

  openModal: (modal: keyof UIState['modals']) => void;
  closeModal: (modal: keyof UIState['modals']) => void;
  closeAllModals: () => void;

  showToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
  clearToasts: () => void;

  setLoading: (key: string, loading: boolean) => void;
  setGlobalLoading: (loading: boolean) => void;

  setActiveGroupId: (id: string | null) => void;
  setActiveTab: (tab: string) => void;

  startRouteChange: () => void;
  endRouteChange: () => void;

  saveFormDraft: (formName: string, data: Record<string, unknown>) => void;
  loadFormDraft: (formName: string) => Record<string, unknown> | undefined;
  clearFormDraft: (formName: string) => void;
  clearAllDrafts: () => void;

  setActivityFilter: (value: ExpenseFilterMode) => void;
  setExpensesFilter: (value: ExpenseFilterMode) => void;
  setCelebrationsEnabled: (enabled: boolean) => void;
  /** The user's own hide/show: updates the store and writes through to the server. */
  setExampleGroupHidden: (hidden: boolean) => void;
  /** Mirror of the server's value (from the loaded profile); never written back. */
  applyServerExampleGroupHidden: (hidden: boolean) => void;
  /**
   * Registers how `setExampleGroupHidden` reaches the server; each app
   * hands in its API client once it exists (web's is a hook, so this
   * can't be a construction-time dep). `null` unregisters.
   */
  setExampleGroupHiddenPersister: (
    persist: ((hidden: boolean) => Promise<unknown>) | null
  ) => void;
  recordExampleGroupLaunch: () => void;
  setExampleScenarioOverride: (key: ExampleScenario['key'] | null) => void;
  setConfettiIntensity: (intensity: ConfettiIntensity) => void;
  setCloudPredictionEnabled: (enabled: boolean) => void;
  pushRecentCurrency: (code: string) => void;
  setActiveImportJobId: (jobId: string | null) => void;
  rememberScanSplitChoice: (groupId: string, choice: ScanSplitChoice) => void;
  clearScanSplitChoices: () => void;
}

export type UIStore = UIState & UIActions;

export interface CreateUIStoreDeps {
  storage: StorageAdapter;
  theme: ThemeAdapter;
}

export function createUIStore(deps: CreateUIStoreDeps) {
  // Per store instance, outside the state: a function has no business in
  // the persisted slice, and swapping it must not notify subscribers.
  let persistExampleGroupHidden:
    ((hidden: boolean) => Promise<unknown>) | null = null;

  return create<UIStore>()(
    devtools(
      persist(
        immer((set, get) => ({
          theme: 'system',
          effectiveTheme: 'light',
          sidebarOpen: true,
          modals: {
            createExpense: false,
            inviteMembers: false,
            settings: false,
            deleteConfirm: false,
          },
          toasts: [],
          loading: {
            global: false,
            actions: {},
          },
          activeGroupId: null,
          activeTab: 'expenses',
          isChangingRoute: false,
          formDrafts: {},
          activityFilter: 'all',
          expensesFilter: 'all',
          celebrationsEnabled: true,
          confettiIntensity: 'subtle',
          cloudPredictionEnabled: true,
          exampleGroupHidden: false,
          exampleGroupLaunches: 0,
          exampleScenarioOverride: null,
          recentCurrencies: [],
          activeImportJobId: null,
          scanSplitChoiceByGroup: {},

          setTheme: theme => {
            set(state => {
              state.theme = theme;
            });

            if (theme === 'system') {
              get().applyTheme(deps.theme.getSystemTheme());
            } else {
              get().applyTheme(theme);
            }
          },

          toggleTheme: () => {
            const currentTheme = get().theme;
            const newTheme = currentTheme === 'light' ? 'dark' : 'light';
            get().setTheme(newTheme);
          },

          applyTheme: theme => {
            set(state => {
              state.effectiveTheme = theme;
            });
            deps.theme.applyTheme(theme);
          },

          toggleSidebar: () => {
            set(state => {
              state.sidebarOpen = !state.sidebarOpen;
            });
          },
          setSidebarOpen: open => {
            set(state => {
              state.sidebarOpen = open;
            });
          },

          openModal: modal => {
            set(state => {
              state.modals[modal] = true;
            });
          },
          closeModal: modal => {
            set(state => {
              state.modals[modal] = false;
            });
          },
          closeAllModals: () => {
            set(state => {
              Object.keys(state.modals).forEach(key => {
                state.modals[key as keyof UIState['modals']] = false;
              });
            });
          },

          showToast: toast => {
            const id = Date.now().toString();
            set(state => {
              state.toasts.push({ ...toast, id });
            });
            if (toast.duration !== 0) {
              setTimeout(() => {
                get().removeToast(id);
              }, toast.duration || 5000);
            }
          },
          removeToast: id => {
            set(state => {
              state.toasts = state.toasts.filter(t => t.id !== id);
            });
          },
          clearToasts: () => {
            set(state => {
              state.toasts = [];
            });
          },

          setLoading: (key, loading) => {
            set(state => {
              if (loading) {
                state.loading.actions[key] = true;
              } else {
                delete state.loading.actions[key];
              }
              state.loading.global =
                Object.keys(state.loading.actions).length > 0;
            });
          },
          setGlobalLoading: loading => {
            set(state => {
              state.loading.global = loading;
            });
          },

          setActiveGroupId: id => {
            set(state => {
              state.activeGroupId = id;
            });
          },
          setActiveTab: tab => {
            set(state => {
              state.activeTab = tab;
            });
          },

          startRouteChange: () => {
            set(state => {
              state.isChangingRoute = true;
            });
          },
          endRouteChange: () => {
            setTimeout(() => {
              set(state => {
                state.isChangingRoute = false;
              });
            }, 100);
          },

          saveFormDraft: (formName, data) => {
            set(state => {
              state.formDrafts[formName] = data;
            });
          },
          loadFormDraft: formName => get().formDrafts[formName],
          clearFormDraft: formName => {
            set(state => {
              delete state.formDrafts[formName];
            });
          },
          clearAllDrafts: () => {
            set(state => {
              state.formDrafts = {};
            });
          },

          setActivityFilter: value => {
            set(state => {
              state.activityFilter = value;
            });
          },
          setExpensesFilter: value => {
            set(state => {
              state.expensesFilter = value;
            });
          },

          setCelebrationsEnabled: enabled => {
            set(state => {
              state.celebrationsEnabled = enabled;
            });
          },

          setConfettiIntensity: intensity => {
            set(state => {
              state.confettiIntensity = intensity;
            });
          },

          setCloudPredictionEnabled: enabled => {
            set(state => {
              state.cloudPredictionEnabled = enabled;
            });
          },

          setExampleGroupHidden: hidden => {
            set(state => {
              state.exampleGroupHidden = hidden;
            });
            // Fire-and-forget: the local value stands if the write fails
            // (offline, old API); the next toggle carries it again.
            void persistExampleGroupHidden?.(hidden).catch(() => undefined);
          },

          applyServerExampleGroupHidden: hidden => {
            set(state => {
              state.exampleGroupHidden = hidden;
            });
          },

          setExampleGroupHiddenPersister: persist => {
            persistExampleGroupHidden = persist;
          },

          pushRecentCurrency: code => {
            set(state => {
              state.recentCurrencies = [
                code,
                ...state.recentCurrencies.filter(c => c !== code),
              ].slice(0, MAX_RECENT_CURRENCIES);
            });
          },

          setActiveImportJobId: jobId => {
            set(state => {
              state.activeImportJobId = jobId;
            });
          },

          rememberScanSplitChoice: (groupId, choice) => {
            set(state => {
              state.scanSplitChoiceByGroup[groupId] = choice;
            });
          },

          clearScanSplitChoices: () => {
            set(state => {
              state.scanSplitChoiceByGroup = {};
            });
          },

          recordExampleGroupLaunch: () => {
            set(state => {
              state.exampleGroupLaunches += 1;
            });
          },

          setExampleScenarioOverride: key => {
            set(state => {
              state.exampleScenarioOverride = key;
            });
          },
        })),
        {
          name: 'ui-storage',
          storage: createJSONStorage(() => deps.storage),
          partialize: state => ({
            theme: state.theme,
            sidebarOpen: state.sidebarOpen,
            activeTab: state.activeTab,
            formDrafts: state.formDrafts,
            activityFilter: state.activityFilter,
            expensesFilter: state.expensesFilter,
            celebrationsEnabled: state.celebrationsEnabled,
            confettiIntensity: state.confettiIntensity,
            cloudPredictionEnabled: state.cloudPredictionEnabled,
            exampleGroupHidden: state.exampleGroupHidden,
            exampleGroupLaunches: state.exampleGroupLaunches,
            recentCurrencies: state.recentCurrencies,
            activeImportJobId: state.activeImportJobId,
            scanSplitChoiceByGroup: state.scanSplitChoiceByGroup,
          }),
        }
      ),
      { name: 'ui-store' }
    )
  );
}
